import { TFunction } from 'i18next';
import { createColumnHelper } from '@tanstack/react-table';
import {
  Badge,
  Flex,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Portal,
  Text,
} from '@chakra-ui/react';
import { defaultIconSize, Icons } from '@texas/components/shared/Icons';
import { ArchivedTooltip } from '@texas/components/shared/tooltip/ArchivedTooltip';
import { ProductGroupListItem } from '@texas/api/endpoints/metadata/productGroupsApi';

export const productGroupTableColumns = (
  t: TFunction,
  onArchiveOpen: (productGroup: ProductGroupListItem) => void,
  onRestore: (id: number) => Promise<void>,
  onEditOpen: (id: number) => void,
) => {
  const columnHelper = createColumnHelper<ProductGroupListItem>();
  const columns = [
    columnHelper.accessor('code', {
      cell: (info) => (
        <Text variant="configuration-code">{info.getValue()}</Text>
      ),
      header: t('general.code'),
      size: 88,
    }),
    columnHelper.accessor('value', {
      cell: (info) => (
        <Flex direction="column">
          <Text variant="configuration-strong">{info.getValue()}</Text>
          {info.row.original.vdcSupport && (
            <Text variant="configuration-accent">
              {t('configuration.vdcSupport')}
            </Text>
          )}
        </Flex>
      ),
      header: t('general.value'),
      size: 200,
    }),
    columnHelper.accessor('dimensions', {
      cell: (info) => (
        <Flex gap={2} flexWrap="wrap">
          {info.getValue().map((d) => (
            <Badge key={d.name} variant="configuration-reference">
              {d.name} - {d.defaultMetric}
            </Badge>
          ))}
        </Flex>
      ),
      header: t('configuration.dimensionGroups'),
      enableSorting: false,
      size: 348,
    }),
    columnHelper.accessor('displayOrder', {
      cell: (info) => info.getValue(),
      header: t('branch.displayOrder'),
      size: 90,
    }),
    columnHelper.accessor('defaultLeadTime', {
      cell: (info) => info.getValue(),
      header: t('configuration.defaultLeadTime'),
      size: 120,
    }),
    columnHelper.accessor('id', {
      cell: (info) => (
        <>
          <Flex justifyContent="end" alignItems="center" gap={4}>
            {info.row.original.archived && (
              <ArchivedTooltip archived={info.row.original.archived} />
            )}
            <Menu isLazy={true} placement="left" variant="entityDirectory">
              <MenuButton float="right" aria-label={t('general.more')}>
                <Icons.DotsHorizontal boxSize={6} />
              </MenuButton>
              <Portal>
                <MenuList>
                  <MenuItem
                    icon={<Icons.Pencil boxSize={defaultIconSize} />}
                    onClick={() => onEditOpen(info.row.original.id)}
                  >
                    {t('general.edit')}
                  </MenuItem>
                  {info.row.original.archived ? (
                    <MenuItem
                      icon={<Icons.Undo boxSize={defaultIconSize} />}
                      onClick={() => onRestore(info.row.original.id)}
                    >
                      {t('general.restore')}
                    </MenuItem>
                  ) : (
                    <MenuItem
                      icon={<Icons.Archive boxSize={defaultIconSize} />}
                      onClick={() => onArchiveOpen(info.row.original)}
                    >
                      {t('general.archive')}
                    </MenuItem>
                  )}
                </MenuList>
              </Portal>
            </Menu>
          </Flex>
        </>
      ),
      header: '',
      enableSorting: false,
      size: 56,
    }),
  ];

  return columns;
};
